import React, { useMemo } from "react";
import Background from "../Background";
import ContainerEffectOverlay from "../ContainerEffectOverlay";
import ProgressiveHeroImage from "../ProgressiveHeroImage";
import {
  SlotRenderer,
  EmptyMediaSlotAffordance,
  SlotCapacityAffordance,
  EditableSlotMediaOverlay,
  getHeroImageStyle,
  useHeroAnimation,
  useHeroVerticalPadding,
} from "./heroModeUtils";
import "./heroModeAnimations.css";
import "./heroDiagonalSplit.css";

/**
 * HeroDiagonalSplit — the Diagonal Split family renderer.
 *
 * Like HeroSplit, this renderer carves the hero into a photo half and
 * a text half — but the seam between the halves is *tilted* instead
 * of straight. The tilt angle and the polygon coordinates depend on
 * the mode id (canonical 25° subtle slash, mirror-direction 25°
 * subtle slash, or 45° corner-to-corner X-pattern) and the number of
 * images slotted into ``gallery`` (1 image → photo on the LEFT only,
 * text on the right; 2 images → photos flank the text on both sides).
 *
 * Geometry overview
 * -----------------
 * Every sibling uses CSS ``clip-path: polygon(...)`` to carve the
 * tilted edges into the photo wrapper. The photo wrapper itself is
 * an absolutely-positioned ``<div>`` whose box-coordinates exactly
 * hug the polygon's bounding box (so the inner ``<img>`` centres on
 * the polygon's visual centre — see ``resolvePolygonGeometry`` for
 * the rationale). The polygon is what restricts the *visible*
 * portion to the desired tilted shape. The text column sits on top
 * in the remaining negative space, anchored to its own padded inset
 * so the text never overflows into the polygon-carved photo region.
 *
 *   * ``diagonal_split`` (canonical, ~25° tilt, PARALLEL cuts that
 *     lean RIGHT — both photos' inner edges run from upper-LEFT to
 *     lower-RIGHT, ``\\`` slope, so the text gap is a parallelogram
 *     that shifts rightward as the eye travels down).
 *
 *     Single image:                Two images:
 *
 *       ┌╲─────────────┐            ┌╲              ╲──┐
 *       │ ╲    text    │            │ ╲    text    ╲   │
 *       │  ╲           │            │  ╲           ╲   │
 *       │ph ╲          │            │ph ╲          ╲ph │
 *       └────╲─────────┘            └────╲          ╲──┘
 *
 *     With parallel cuts the text gap has CONSTANT WIDTH at every
 *     vertical position (no narrow midline like the earlier
 *     mirrored geometry), so the heading + body + CTA all share
 *     the same horizontal real-estate regardless of where they
 *     sit vertically. The trade-off vs. mirrored cuts: the gap
 *     visually slides to one side as you scan top→bottom, which
 *     reads as a single coherent diagonal motion rather than two
 *     opposing slopes — the editorial "the page is tilting" feel
 *     this family is named for.
 *
 *   * ``diagonal_split_left`` — same parallel-cut geometry as
 *     ``diagonal_split`` but the slope direction is mirrored
 *     (``//`` slope, both inner edges run upper-RIGHT → lower-
 *     LEFT, text gap shifts leftward as the eye travels down).
 *     Identical photo widths and tuning constants — only the
 *     direction parameter flips, so the two siblings are guaranteed
 *     to be visually balanced when picked side-by-side in the
 *     editor.
 *
 *   * ``diagonal_split_x`` (super-diagonal, 45°)
 *
 *     Single image:                Two images:
 *
 *       ┌─────────────┐             ┌──────────────┐
 *       │ ╲           │             │ ╲      photo │
 *       │   ╲   text  │             │   ╲     R    │
 *       │ ph  ╲       │             │ ph  ╲ text   │
 *       │ oto   ╲     │             │ oto   ╲      │
 *       └─────────────┘             │  L      ╲    │
 *                                   └──────────────┘
 *
 *     Two-image variant uses INSET=50% so the two corner
 *     triangles meet exactly along the anti-diagonal — together
 *     they cover half the hero, leaving the other half as a
 *     clean diagonal band that seats the text column. Earlier
 *     values that overlapped the band perpendicular to the
 *     diagonal squeezed the text region so badly that the
 *     content overlapped the triangle interiors.
 *
 *     The corner-cut polygons make the photos read as right
 *     triangles. The two-image variant places one triangle in the
 *     top-left corner and another in the bottom-right corner so the
 *     text column wraps along the diagonal between them — an X-shape
 *     composition with the text living in the visible diagonal band.
 *
 * Why this lives in one renderer (not two)
 * ----------------------------------------
 * The two siblings differ only in:
 *   * The polygon coordinates (gentle tilt vs. corner-to-corner).
 *   * For the X variant: the second image's anchor corner.
 * Everything else (slot rendering, text column inset, empty-state
 * affordance, mobile fallback) is shared. Forking the file would
 * trigger every future tweak to be made twice — same one-renderer-
 * per-family philosophy ``HeroSplit`` / ``HeroPhotoFloat`` /
 * ``HeroPhotoDrift`` use.
 */

// ---------------------------------------------------------------------------
// Mode-id constants
// ---------------------------------------------------------------------------
//
// Centralised so the dispatch on ``item.hero_mode`` reads from a
// single source. A future addition (e.g. ``diagonal_split_topright``
// for the mirror anchor) lands in this constant block first before
// touching the polygon factory.

const MODE_DIAGONAL_SUBTLE = "diagonal_split";
const MODE_DIAGONAL_LEFT = "diagonal_split_left";
const MODE_DIAGONAL_X = "diagonal_split_x";

// Slope direction for the subtle-cut siblings. ``"right"`` produces
// ``\\`` cuts (inner edges run upper-LEFT → lower-RIGHT, text gap
// shifts rightward as you scan down — the canonical
// ``diagonal_split`` identity). ``"left"`` produces ``//`` cuts
// (mirror direction — the ``diagonal_split_left`` identity). The
// two directions are enumerated explicitly (rather than expressed
// as a +1/-1 sign) so the call sites that branch on direction stay
// human-readable when revisited.
const SUBTLE_DIR_RIGHT = "right";
const SUBTLE_DIR_LEFT = "left";

function resolveSubtleDirection(modeId) {
  return modeId === MODE_DIAGONAL_LEFT ? SUBTLE_DIR_LEFT : SUBTLE_DIR_RIGHT;
}

// Defensive cap mirroring ``DiagonalSplitMode._DIAGONAL_SPLIT_MAX_IMAGES``
// in the backend definition. Past 2 images the polygons would
// overlap each other and lose the framing intent — the family's
// visual identity is "1 photo with a tilt" or "2 photos framing
// the text", and the cap keeps that identity honest even if the
// editor allowed extra items by mistake.
const MAX_IMAGES = 2;

// ---------------------------------------------------------------------------
// Per-breakpoint tuning
// ---------------------------------------------------------------------------
//
// ``contentPx`` / ``contentPy`` — outer padding for the text column
//   so heading / body / CTA never butt against the visible hero
//   edge. Mobile uses tighter values because the hero shrinks.
// ``stackedTrayHeight`` — height of the photo block when the layout
//   collapses to stacked (mobile). Picked so the photo reads as a
//   meaningful visual block without dominating the whole phone
//   screen.
// ``isStacked`` — controls whether the photos render as a tilted
//   overlay (desktop) or as a stacked rectangular block above the
//   text (mobile). The diagonal cut is only a visual win when the
//   hero is wide enough for the slope to have room to breathe;
//   on phones the trapezoidal text column gets uncomfortably
//   narrow, so we collapse to a regular Split-style stack.
const BP_CONFIG = {
  extra_large: { contentPx: 80, contentPy: 64, isStacked: false, stackedTrayHeight: 320 },
  large:       { contentPx: 64, contentPy: 56, isStacked: false, stackedTrayHeight: 300 },
  medium:      { contentPx: 48, contentPy: 48, isStacked: false, stackedTrayHeight: 280 },
  small:       { contentPx: 24, contentPy: 32, isStacked: true,  stackedTrayHeight: 260 },
  extra_small: { contentPx: 16, contentPy: 24, isStacked: true,  stackedTrayHeight: 220 },
};

// ---------------------------------------------------------------------------
// Polygon geometry
// ---------------------------------------------------------------------------
//
// Each polygon is expressed as an array of ``[x%, y%]`` points in
// HERO coordinates (i.e. percentages of the hero's width and
// height). Coordinates run clockwise from the top-left corner of
// the polygon's outline.
//
// At render time ``resolvePolygonGeometry`` derives TWO things from
// the absolute point list:
//   1) The polygon's BOUNDING BOX in hero coordinates — used to
//      size and position the photo wrapper so the wrapper exactly
//      hugs the polygon's outer extent. Earlier versions made every
//      wrapper full-bleed (``inset: 0``) and relied on ``clip-path``
//      alone to carve out the visible region; the consequence was
//      that the inner ``<img>`` (with ``object-fit: cover``)
//      centered itself on the HERO's centre, so the visible polygon
//      slice ended up showing the LEFT-most strip of a left-anchored
//      image (which usually crops out the subject's face). Sizing
//      the wrapper to the polygon's bbox makes ``object-fit: cover``
//      centre the image on the bbox's centre, which is also the
//      polygon's visual centre — so the visible polygon now shows
//      the MIDDLE of the source image, not the edge.
//   2) The polygon points re-expressed in the wrapper's LOCAL
//      coordinates (where 0%-100% spans the bbox, not the hero).
//      This is what ``clip-path: polygon(...)`` consumes.
//
// The ``offsetPct`` / ``BASE_PCT`` knobs control how far the
// polygon edges deviate from the canonical 50% / 0% positions. For
// the subtle (25°) cut they're small; for the X (45°) cut they
// push the polygon corners to the opposite hero edges.

// Subtle 25° cut tuning constants.
//
// ``SUBTLE_BASE_PCT`` is the photo's average horizontal extent
// (centre of the polygon's tilted edge along the inner side).
// Intentionally well under 50% so the photo occupies the MINORITY
// of the hero's width on average — the composition is "small photo
// cut diagonally next to a generous text column", not "half-photo
// / half-text". Earlier values that pushed the photo to ~50% width
// ate so much horizontal room that the text column couldn't seat a
// heading without overflowing into the photo region.
//
// ``SUBTLE_OFFSET_PCT`` is the slope amplitude — half of the total
// horizontal travel of the diagonal edge from top to bottom. Kept
// gentle so the wedge tapers slowly and the text inset (which has
// to clear the photo's WIDEST edge) still leaves a usable text
// width.
const SUBTLE_OFFSET_PCT = 8;
const SUBTLE_BASE_PCT = 30;

// Subtle 25° single-image polygon. Photo always anchors on the
// LEFT outer edge of the hero (``SINGLE_IMAGE_PHOTO_SIDE``); the
// inner edge slope is what differs between the two direction
// siblings:
//
//   * ``"right"`` (``\\`` slope) — top-right at (BASE-OFFSET, 0),
//     bottom-right at (BASE+OFFSET, 100). Photo NARROWS at the
//     top, WIDENS at the bottom. Inner edge runs upper-LEFT →
//     lower-RIGHT.
//
//   * ``"left"`` (``//`` slope) — top-right at (BASE+OFFSET, 0),
//     bottom-right at (BASE-OFFSET, 100). Photo WIDENS at the
//     top, NARROWS at the bottom. Inner edge runs upper-RIGHT →
//     lower-LEFT.
//
// Both directions use IDENTICAL ``BASE`` and ``OFFSET`` so the
// photo's average width (and therefore the family's visual
// "weight") is the same regardless of direction — only the
// orientation differs. This means switching between the two
// siblings in the editor produces a strict mirror image, not a
// resize.
function buildSubtleSinglePoints(direction) {
  const wide = SUBTLE_BASE_PCT + SUBTLE_OFFSET_PCT;
  const narrow = SUBTLE_BASE_PCT - SUBTLE_OFFSET_PCT;
  if (direction === SUBTLE_DIR_LEFT) {
    return [
      [0, 0],
      [wide, 0],
      [narrow, 100],
      [0, 100],
    ];
  }
  // SUBTLE_DIR_RIGHT — narrow at top, wide at bottom.
  return [
    [0, 0],
    [narrow, 0],
    [wide, 100],
    [0, 100],
  ];
}

// Subtle 25° two-image cuts. Both photos run their inner-edge
// slopes in the SAME direction (PARALLEL cuts — the family's new
// canonical geometry). The text gap is therefore a parallelogram
// of CONSTANT WIDTH that slides laterally as the eye scans down,
// reading as a single coherent diagonal motion. The earlier
// MIRRORED geometry (left photo ``//``, right photo ``\\``)
// produced a diamond-shaped gap that was narrowest at the top and
// widest at the bottom — visually busy and forced the text inset
// to use the worst-case top width on both sides, wasting the
// extra room at the bottom.
//
// ``TWO_IMAGE_AVG_INNER`` — average x of each photo's inner edge
//   (centre of the slope). Photos cap at ~20% of width on average,
//   leaving a text gap that's a constant ~60% wide. Picked so the
//   composition reads as "two photos framing a generous text
//   column", not "two photos eating the centre".
//
// ``TWO_IMAGE_INNER_OFFSET`` — half the slope amplitude. Smaller
//   than the single-image offset because two slopes already
//   carry more visual energy than one — keeping the slope gentle
//   per-photo prevents the composition from feeling overly
//   "leaning".
//
// The two photos use the SAME tuning constants, just mirrored
// across the vertical centre. This guarantees that the
// composition stays visually balanced left-vs-right: the average
// photo width on each side is identical and the slope magnitude
// matches.
const TWO_IMAGE_AVG_INNER = 20;
const TWO_IMAGE_INNER_OFFSET = 5;

function buildSubtleTwoImageLeftPoints(direction) {
  const wide = TWO_IMAGE_AVG_INNER + TWO_IMAGE_INNER_OFFSET;
  const narrow = TWO_IMAGE_AVG_INNER - TWO_IMAGE_INNER_OFFSET;
  if (direction === SUBTLE_DIR_LEFT) {
    // ``//`` slope — wide at top, narrow at bottom.
    return [
      [0, 0],
      [wide, 0],
      [narrow, 100],
      [0, 100],
    ];
  }
  // SUBTLE_DIR_RIGHT — ``\\`` slope: narrow at top, wide at bottom.
  return [
    [0, 0],
    [narrow, 0],
    [wide, 100],
    [0, 100],
  ];
}

function buildSubtleTwoImageRightPoints(direction) {
  const wide = TWO_IMAGE_AVG_INNER + TWO_IMAGE_INNER_OFFSET;
  const narrow = TWO_IMAGE_AVG_INNER - TWO_IMAGE_INNER_OFFSET;
  if (direction === SUBTLE_DIR_LEFT) {
    // ``//`` slope (parallel with the left photo). The right
    // photo's INNER (left) edge runs upper-RIGHT → lower-LEFT, so
    // top-left is the wider x value (= 100 - narrow) and
    // bottom-left is the narrower x value (= 100 - wide).
    return [
      [100 - narrow, 0],
      [100, 0],
      [100, 100],
      [100 - wide, 100],
    ];
  }
  // SUBTLE_DIR_RIGHT — ``\\`` slope (parallel with the left
  // photo). The right photo's INNER (left) edge runs upper-LEFT →
  // lower-RIGHT, so top-left is the narrower x value (= 100 -
  // wide) and bottom-left is the wider x value (= 100 - narrow).
  return [
    [100 - wide, 0],
    [100, 0],
    [100, 100],
    [100 - narrow, 100],
  ];
}

// X (45°) cut — single image anchored in the TOP-LEFT corner. The
// polygon runs from top-left → top-right → bottom-left, producing
// a true right triangle that takes up roughly half the hero on a
// 45° diagonal. The text column wraps in the opposite triangle's
// negative space (no clip-path needed on the text container — it
// just inset-padded to stay clear of the photo triangle).
//
// The X-single bbox IS the full hero (the triangle's vertices
// span 0% → 100% on both axes), so for this case the wrapper
// remains full-bleed and the image still appears centered on the
// hero — no different from the earlier behaviour. That's
// acceptable here because the visible triangle covers half the
// hero, so the image content the triangle reveals already
// includes the source's centre.
function buildXSinglePoints() {
  return [
    [0, 0],
    [100, 0],
    [0, 100],
  ];
}

// X (45°) cut — two images. Triangle 1 in the TOP-LEFT corner;
// Triangle 2 in the BOTTOM-RIGHT corner. The text column lives in
// the diagonal band between them (the band is the area that's
// neither in triangle 1 nor in triangle 2).
//
// Triangle 1: top-left → top-right → bottom-left (same as the
// single-image polygon above, but its diagonal edge is shifted
// inward so the band has room to breathe).
// Triangle 2: top-right → bottom-right → bottom-left (mirror of
// triangle 1 around the centre point).
//
// ``X_TWO_IMG_INSET`` controls how aggressively the triangles eat
// the hero. The two triangles sit in opposite corners (top-left
// and bottom-right) with their hypotenuses running parallel — at
// INSET=50% the hypotenuses are exactly the two halves of the
// anti-diagonal, the triangles together cover 50% of the hero,
// and the remaining 50% is a clean diagonal band that can seat
// the text column. Higher values overlap the band perpendicular-
// to-the-diagonal so the text gets squeezed; lower values shrink
// the triangles into mere corner accents that lose the X-pattern
// reading. 50% is the sweet spot that keeps both halves
// (image identity + text legibility) intact.
const X_TWO_IMG_INSET = 50;

function buildXTwoImageTopLeftPoints() {
  return [
    [0, 0],
    [X_TWO_IMG_INSET, 0],
    [0, X_TWO_IMG_INSET],
  ];
}

function buildXTwoImageBottomRightPoints() {
  const start = 100 - X_TWO_IMG_INSET;
  return [
    [100, start],
    [100, 100],
    [start, 100],
  ];
}

// Resolve the absolute hero-coordinate polygon points for a given
// (mode, image index, total). Centralising the dispatch keeps the
// JSX simple — each photo wrapper just hands its slot's points to
// ``resolvePolygonGeometry`` to derive both its bbox-anchored
// position and its local-coordinate clip-path.
function resolvePolygonPoints({ modeId, imageIndex, imageCount }) {
  const isX = modeId === MODE_DIAGONAL_X;
  const direction = resolveSubtleDirection(modeId);
  if (imageCount === 1) {
    return isX ? buildXSinglePoints() : buildSubtleSinglePoints(direction);
  }
  // Two-image case — first image left/top-left, second image
  // right/bottom-right.
  if (imageIndex === 0) {
    return isX
      ? buildXTwoImageTopLeftPoints()
      : buildSubtleTwoImageLeftPoints(direction);
  }
  return isX
    ? buildXTwoImageBottomRightPoints()
    : buildSubtleTwoImageRightPoints(direction);
}

// ---------------------------------------------------------------------------
// Polygon → wrapper geometry
// ---------------------------------------------------------------------------
//
// Given a polygon expressed in HERO coordinates (an array of
// ``[x%, y%]`` pairs, 0-100 on each axis), derive:
//   * ``wrapperStyle`` — absolute positioning (top/left/width/height
//     in %) that exactly hugs the polygon's bounding box. The
//     wrapper sits inside the photo layer (which is itself
//     ``position: absolute; inset: 0`` over the hero), so the %
//     values are relative to the hero's box.
//   * ``clipPath`` — the same polygon re-expressed in the wrapper's
//     LOCAL coordinates (where 0%-100% spans the bbox, not the
//     hero). The carved shape is identical to before — only the
//     coordinate basis changes.
//
// Why the bbox sizing matters: with the wrapper sized to the
// bbox, the inner ``<img class="hero-diagonal-split-img">`` (which
// uses ``object-fit: cover``) centres its content on the WRAPPER's
// centre, which now coincides with the polygon's visual centre.
// The visible polygon slice therefore shows the MIDDLE of the
// source image, not the edge — matching what the eye expects when
// it sees a cropped image and what the family's identity ("a
// portrait carved diagonally next to a text column") implies.
function resolvePolygonGeometry(absolutePoints) {
  const xs = absolutePoints.map((p) => p[0]);
  const ys = absolutePoints.map((p) => p[1]);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const width = maxX - minX;
  const height = maxY - minY;
  // Defensive: a degenerate bbox (zero width or height) should
  // never happen given the polygon definitions above, but if it
  // does we fall back to a non-clipping rectangle so the image is
  // visible rather than silently invisible.
  const safeWidth = width > 0 ? width : 100;
  const safeHeight = height > 0 ? height : 100;
  const localPoints = absolutePoints.map(([x, y]) => [
    width > 0 ? ((x - minX) / width) * 100 : 0,
    height > 0 ? ((y - minY) / height) * 100 : 0,
  ]);
  const clipPath = `polygon(${localPoints
    .map(([x, y]) => `${x.toFixed(4)}% ${y.toFixed(4)}%`)
    .join(", ")})`;
  return {
    wrapperStyle: {
      top: `${minY}%`,
      left: `${minX}%`,
      width: `${safeWidth}%`,
      height: `${safeHeight}%`,
    },
    clipPath,
  };
}

// ---------------------------------------------------------------------------
// Text column inset — keeps the text out of the photo polygon(s)
// ---------------------------------------------------------------------------
//
// The text column is rendered on top of the photo layer. To prevent
// the heading / body / CTA from overflowing into the diagonally-cut
// photo region, we apply an asymmetric inline padding that hugs the
// non-photo side of the composition. The inset is a sum of the
// breakpoint's ``contentPx`` (cosmetic breathing room) PLUS a
// percentage that reserves the polygon's WIDEST horizontal extent
// on that side so the text never crosses the visible polygon edge.
//
// With the family's PARALLEL-cut geometry (both photo cuts running
// in the same direction), the resulting gap is a parallelogram of
// CONSTANT WIDTH that slides laterally as the eye scans down.
// That symmetry lets us position the text column "next to" the
// photo on each side: each side's inset reserves only that side's
// photo extent — the SAME ``TWO_IMAGE_AVG_INNER + OFFSET`` value
// on the LEFT (left photo's widest inner edge) and on the RIGHT
// (right photo's widest inner edge). Because both photos have the
// same dimensions, the inset value is identical on both sides
// numerically, but conceptually it's "clear the photo on this
// side", not "clear the worst-case anywhere". This keeps the text
// column visually wedged between the photos rather than recessed
// to a smaller central rectangle.
//
// For the X cut the geometry isn't parallel (right triangles in
// opposite corners with hypotenuses running along the
// anti-diagonal), so a symmetric inset based on half the corner
// inset is what keeps the content inside the diagonal band's
// narrowest rectangular inscription — same reasoning as before.

// Shared helper: which side of a single-image hero does the photo
// sit on. Today it's always LEFT for every subtle / X single —
// hoisted into a constant so a future "_right"-anchored sibling
// can flip the inset side from a single source of truth.
const SINGLE_IMAGE_PHOTO_SIDE = "left";

function resolveTextInset({ modeId, imageCount, contentPx }) {
  const isX = modeId === MODE_DIAGONAL_X;
  if (imageCount === 0) {
    return { paddingLeft: contentPx, paddingRight: contentPx };
  }
  if (imageCount === 1) {
    if (isX) {
      // X single: right triangle in the TOP-LEFT corner with its
      // hypotenuse running from (100,0) to (0,100). The triangle
      // covers exactly half the hero — everything on the
      // top-left side of the anti-diagonal. The text content is
      // vertically centered in the hero, so the heading sits a
      // bit ABOVE y=50% and the buttons sit a bit BELOW. The
      // worst-case x constraint over the content's vertical
      // range is at the TOP of the content (closest to the
      // triangle's widest extent). To keep the heading clear
      // we reserve ``50%`` of the hero width as left padding —
      // the triangle's extent at vertical centre — plus
      // ``contentPx`` breathing room.
      const inset = `calc(50% + ${contentPx}px)`;
      return SINGLE_IMAGE_PHOTO_SIDE === "left"
        ? { paddingLeft: inset, paddingRight: `${contentPx}px` }
        : { paddingLeft: `${contentPx}px`, paddingRight: inset };
    }
    // Subtle single (both directions): photo on the left, text on
    // the right. The photo's WIDEST extent sits at
    // ``SUBTLE_BASE_PCT + SUBTLE_OFFSET_PCT`` (= 38% today) at
    // either the TOP (``//`` slope, ``diagonal_split_left``) or
    // the BOTTOM (``\\`` slope, canonical ``diagonal_split``). We
    // reserve that as the inset on the photo side regardless of
    // direction so the text NEVER crosses the visible photo edge
    // at the photo's widest point. The OTHER side gets only the
    // cosmetic ``contentPx`` so the text column hugs the hero's
    // outer edge — that's what makes the text feel "next to" the
    // photo rather than centered in the remaining negative
    // space.
    const wideEdge = SUBTLE_BASE_PCT + SUBTLE_OFFSET_PCT;
    const inset = `calc(${wideEdge}% + ${contentPx}px)`;
    return SINGLE_IMAGE_PHOTO_SIDE === "left"
      ? { paddingLeft: inset, paddingRight: `${contentPx}px` }
      : { paddingLeft: `${contentPx}px`, paddingRight: inset };
  }
  // Two-image case.
  if (isX) {
    // X two images: right triangles in opposite corners
    // (top-left and bottom-right) with their hypotenuses running
    // parallel along the anti-diagonal. With INSET = 50% the
    // hypotenuses are exactly y = 50 - x and y = 150 - x; the
    // safe diagonal band between them is widest at vertical
    // centre and tapers toward the corner triangles. The text
    // content is vertically centered, so the WORST-CASE x
    // constraint over its vertical range is at the TOP of the
    // content (top-left triangle bounds it on the left) and at
    // the BOTTOM (bottom-right triangle bounds it on the
    // right). We reserve a symmetric inset on each side equal
    // to half the INSET value (= 25% with INSET=50) so the
    // content stays inside the diagonal band's narrowest
    // rectangular inscription. Symmetry on both sides keeps
    // the text column visually centered in the diagonal band
    // and matches the X-pattern's mirror geometry.
    const halfInset = `calc(${X_TWO_IMG_INSET / 2}% + ${contentPx}px)`;
    return { paddingLeft: halfInset, paddingRight: halfInset };
  }
  // Subtle two images (both ``diagonal_split`` and
  // ``diagonal_split_left``): photos flank the text on both
  // sides with PARALLEL inner-edge slopes. Each photo's WIDEST
  // inner edge sits at ``TWO_IMAGE_AVG_INNER + OFFSET`` (= 25%
  // today) — at the TOP for ``//`` direction and the BOTTOM for
  // ``\\`` direction. Either way the text column needs at least
  // that much padding on each side to clear the visible photo
  // edge at its widest extent. Using the SAME value on both
  // sides keeps the text column rectangular (no jarring
  // per-line indentation) and gives the heading enough room to
  // breathe even if it spans two lines. The constant-width gap
  // means the text effectively SITS NEXT TO each photo edge
  // without recessing into a smaller central rectangle the way
  // the earlier mirrored geometry forced — that's the visual
  // "text alongside the image" reading the family is named for.
  const wideEdge = TWO_IMAGE_AVG_INNER + TWO_IMAGE_INNER_OFFSET;
  const inset = `calc(${wideEdge}% + ${contentPx}px)`;
  return { paddingLeft: inset, paddingRight: inset };
}

function addCssLength(baseValue, extraValue) {
  if (!extraValue) return baseValue;
  if (!baseValue) return extraValue;
  const base = typeof baseValue === "number" ? `${baseValue}px` : baseValue;
  return `calc(${base} + ${extraValue})`;
}

function combineHorizontalInsets(slotInset, contentInsetStyle) {
  const {
    paddingLeft: pagePaddingLeft,
    paddingRight: pagePaddingRight,
    ...restContentInset
  } = contentInsetStyle || {};

  return {
    ...restContentInset,
    paddingLeft: addCssLength(slotInset.paddingLeft, pagePaddingLeft),
    paddingRight: addCssLength(slotInset.paddingRight, pagePaddingRight),
  };
}

function slotVerticalProgress(slotName) {
  if (slotName === "heading") return 0.2;
  if (slotName === "cta") return 0.8;
  return 0.5;
}

function interpolate(start, end, progress) {
  return start + (end - start) * progress;
}

function resolveSubtleSlotInset({ modeId, imageCount, contentPx, slotName }) {
  const direction = resolveSubtleDirection(modeId);
  const progress = slotVerticalProgress(slotName);

  if (imageCount === 1) {
    const topEdge = direction === SUBTLE_DIR_LEFT
      ? SUBTLE_BASE_PCT + SUBTLE_OFFSET_PCT
      : SUBTLE_BASE_PCT - SUBTLE_OFFSET_PCT;
    const bottomEdge = direction === SUBTLE_DIR_LEFT
      ? SUBTLE_BASE_PCT - SUBTLE_OFFSET_PCT
      : SUBTLE_BASE_PCT + SUBTLE_OFFSET_PCT;
    const edgePct = interpolate(topEdge, bottomEdge, progress);

    return {
      paddingLeft: `calc(${edgePct}% + ${contentPx}px)`,
      paddingRight: `${contentPx}px`,
    };
  }

  const narrow = TWO_IMAGE_AVG_INNER - TWO_IMAGE_INNER_OFFSET;
  const wide = TWO_IMAGE_AVG_INNER + TWO_IMAGE_INNER_OFFSET;
  const leftTop = direction === SUBTLE_DIR_LEFT ? wide : narrow;
  const leftBottom = direction === SUBTLE_DIR_LEFT ? narrow : wide;
  const rightTop = direction === SUBTLE_DIR_LEFT ? narrow : wide;
  const rightBottom = direction === SUBTLE_DIR_LEFT ? wide : narrow;
  const leftEdgePct = interpolate(leftTop, leftBottom, progress);
  const rightEdgePct = interpolate(rightTop, rightBottom, progress);

  return {
    paddingLeft: `calc(${leftEdgePct}% + ${contentPx}px)`,
    paddingRight: `calc(${rightEdgePct}% + ${contentPx}px)`,
  };
}

function resolveSlotTextInset({ modeId, imageCount, contentPx, slotName }) {
  if (modeId === MODE_DIAGONAL_X || imageCount === 0) {
    return resolveTextInset({ modeId, imageCount, contentPx });
  }

  return resolveSubtleSlotInset({ modeId, imageCount, contentPx, slotName });
}

function resolveContentAlignment(modeId) {
  if (modeId === MODE_DIAGONAL_X) return "center";
  return modeId === MODE_DIAGONAL_LEFT ? "right" : "left";
}

// ---------------------------------------------------------------------------
// HeroDiagonalSplit main renderer
// ---------------------------------------------------------------------------

const HeroDiagonalSplit = ({ item, heroBase, heroSlots }) => {
  const {
    heroRootRef, containerClasses, containerStyle, fullBleedStyle,
    contentInsetStyle, responsiveHeight, baseHeightPx, bp,
  } = heroBase;

  const isAnimated = heroSlots?.isAnimated || false;
  const gallerySlotData = heroSlots?.getSlot("gallery");
  const galleryItems = useMemo(() => {
    if (!gallerySlotData) return [];
    if (Array.isArray(gallerySlotData)) return gallerySlotData;
    return [gallerySlotData];
  }, [gallerySlotData]);
  const images = useMemo(
    () => galleryItems.filter((it) => it?.type === "image").slice(0, MAX_IMAGES),
    [galleryItems],
  );
  const imageCount = images.length;

  const config = BP_CONFIG[bp] || BP_CONFIG.medium;
  const modeId = item?.hero_mode;
  const isXVariant = modeId === MODE_DIAGONAL_X;
  const isLeftDirection = modeId === MODE_DIAGONAL_LEFT;

  // Vertical break-out for the photo layer. Without this, the
  // photo wrapper sits inside the hero root's padding box and a
  // band of background is visible above/below the polygon — see
  // ``useHeroVerticalPadding`` (used by HeroSplit / HeroPhotoBanner
  // for the same purpose) for the full reasoning. We only apply
  // the break-out on the diagonal (desktop) layout: on the
  // stacked mobile fallback the photo is a regular rectangle that
  // respects the hero's padding.
  const heroPad = useHeroVerticalPadding(heroBase.heroRootRef);
  const photoBreakoutStyle = !config.isStacked
    ? {
        top: heroPad.top ? `-${heroPad.top}px` : 0,
        bottom: heroPad.bottom ? `-${heroPad.bottom}px` : 0,
      }
    : null;

  const entrance = heroSlots?.animatedEntrance || "fade-up";
  const contentAnim = useHeroAnimation(isAnimated, entrance, { delay: 200 });

  // Clip the hero root when media is present. The photo layer uses
  // ``useHeroVerticalPadding`` break-out (negative top/bottom) so
  // polygons fill the padded band; without root clipping, that layer
  // extends past the hero box while ``useHeroBase`` may attach
  // ``overflow-visible`` (non-rounded containers). Match
  // ``HeroCircleSplit``'s ``clippedHeroStyle`` pattern.
  const clippedHeroStyle = {
    ...containerStyle,
    ...fullBleedStyle,
    overflow: "hidden",
  };

  const heroBackgroundLayer = (
    <Background
      bgImage={heroBase.bgImage}
      imageStyleClasses={heroBase.imageStyleClasses}
      imageInlineStyles={heroBase.imageInlineStyles}
      colorClass={heroBase.colorClass}
      colorStyle={heroBase.colorStyle}
      jsPatternLayer={heroBase.jsPatternLayer}
    />
  );

  // Empty state mirrors Photo Banner / Photo Drift / Photo Float:
  // no diagonal layer mounts until at least one image is
  // available — pinning the absence here guards against a
  // regression that would render a polygon-clipped empty box on
  // first mount.
  if (imageCount === 0) {
    return (
      <div
        ref={heroRootRef}
        data-hero-root="true"
        className={containerClasses}
        style={{ ...containerStyle, ...fullBleedStyle }}
      >
        {heroBackgroundLayer}
        <ContainerEffectOverlay data={heroBase.data} />
        <div
          className={
            responsiveHeight
              ? "relative z-10 flex items-center justify-center w-full"
              : "absolute inset-0 z-10 flex items-center justify-center"
          }
          style={responsiveHeight ? { minHeight: `${baseHeightPx}px` } : undefined}
        >
          <div
            ref={contentAnim.ref}
            className={`flex flex-col gap-4 w-full max-w-2xl mx-auto ${contentAnim.className}`}
            style={{ padding: config.contentPx, ...contentAnim.style, ...contentInsetStyle }}
          >
            <EmptyMediaSlotAffordance
              heroSlots={heroSlots}
              heroId={item?.id}
              slotName="gallery"
              order={0}
            />
            <SlotRenderer slotName="heading" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={1} />
            <SlotRenderer slotName="body" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={2} />
            <SlotRenderer slotName="cta" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={3} />
          </div>
        </div>
      </div>
    );
  }

  // Mobile fallback: same images, but rendered as a stacked
  // rectangle above the text — no diagonal cut. Same mobile-
  // collapse reasoning HeroSplit uses (the diagonal slope only
  // reads cleanly when the hero is wide enough for the polygon
  // to have room to breathe; on phones the resulting trapezoidal
  // text column gets uncomfortably narrow). The cluster shape is
  // a horizontal row of photo thumbnails so a 2-image hero still
  // shows BOTH photos on mobile, just without the cut.
  if (config.isStacked) {
    const hasSplitMobileTray = imageCount > 1;
    const mobileTrayHeight = hasSplitMobileTray
      ? Math.round(config.stackedTrayHeight / 2)
      : config.stackedTrayHeight;
    const renderMobileTray = (trayImages, keyPrefix) => (
      <div
        className="hero-diagonal-split-mobile-tray flex w-full"
        style={{ height: `${mobileTrayHeight}px` }}
      >
        {trayImages.map((img, idx) => {
          const src = img.cropped_image_url || img.image_url;
          const { className: filterClass, style: filterStyle } =
            getHeroImageStyle(img);
          return (
            <div
              key={`${keyPrefix}-${img.id || idx}-${idx}`}
              className="hero-diagonal-split-mobile-photo relative"
              style={{ width: `${100 / trayImages.length}%` }}
            >
              {src ? (
                <ProgressiveHeroImage
                  src={src}
                  alt={img.alt_text || ""}
                  loading={idx === 0 ? "eager" : "lazy"}
                  draggable={false}
                  className={`hero-diagonal-split-img ${filterClass}`.trim()}
                  style={filterStyle}
                />
              ) : (
                <div className="hero-diagonal-split-img hero-diagonal-split-img--empty" />
              )}
              <EditableSlotMediaOverlay slotData={img} heroItem={item} />
            </div>
          );
        })}
      </div>
    );
    const topMobileImages = hasSplitMobileTray ? images.slice(0, 1) : images;
    const bottomMobileImages = hasSplitMobileTray ? images.slice(1, 2) : [];

    return (
      <div
        ref={heroRootRef}
        data-hero-root="true"
        className={containerClasses}
        style={clippedHeroStyle}
      >
        {heroBackgroundLayer}
        <ContainerEffectOverlay data={heroBase.data} />

        <div
          className={
            responsiveHeight
              ? "relative z-10 flex flex-col w-full"
              : "absolute inset-0 z-10 flex flex-col"
          }
          style={responsiveHeight ? { minHeight: `${baseHeightPx}px` } : undefined}
        >
          {renderMobileTray(topMobileImages, "diag-mobile-top")}

          <div
            ref={contentAnim.ref}
            className={`flex flex-col justify-center gap-4 flex-1 min-w-0 ${contentAnim.className}`}
            style={{
              paddingLeft: config.contentPx,
              paddingRight: config.contentPx,
              paddingTop: config.contentPy,
              paddingBottom: config.contentPy,
              ...contentAnim.style,
            }}
          >
            <SlotRenderer slotName="heading" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={1} />
            <SlotRenderer slotName="body" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={2} />
            <SlotRenderer slotName="cta" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={3} />
          </div>

          {bottomMobileImages.length > 0
            ? renderMobileTray(bottomMobileImages, "diag-mobile-bottom")
            : null}
        </div>
      </div>
    );
  }

  // Desktop layout — diagonally-cut photo overlay + text column
  // on top. The photo layer is absolutely positioned at the hero
  // root level so the polygons can extend to the hero's outer
  // bounds; the text column sits above it with an asymmetric
  // padding that keeps the text clear of the visible polygon
  // region.
  const slotInsetFor = (slotName) => combineHorizontalInsets(
    resolveSlotTextInset({
      modeId,
      imageCount,
      contentPx: config.contentPx,
      slotName,
    }),
    contentInsetStyle,
  );
  const contentAlignment = resolveContentAlignment(modeId);
  const slotClassName = `hero-diagonal-split-slot hero-diagonal-split-slot--align-${contentAlignment} box-border min-w-0 w-full`;

  return (
    <div
      ref={heroRootRef}
      data-hero-root="true"
      className={containerClasses}
      style={clippedHeroStyle}
    >
      {heroBackgroundLayer}
      <ContainerEffectOverlay data={heroBase.data} />

      {/* Photo layer — one wrapper per image. Each wrapper is a
          full-bleed absolutely-positioned ``<div>``; ``clip-path``
          carves the wrapper down to the visible polygon. Wrappers
          share the same z-index plane so a 2-image hero shows both
          polygons side-by-side without one occluding the other
          (their polygons don't overlap in the resting layout). */}
      <div
        className={`hero-diagonal-split-photo-layer ${
          isXVariant
            ? "hero-diagonal-split--x"
            : isLeftDirection
              ? "hero-diagonal-split--subtle hero-diagonal-split--subtle-left"
              : "hero-diagonal-split--subtle hero-diagonal-split--subtle-right"
        }`}
        style={{
          // Vertical break-out matches HeroSplit's media half so
          // the photo bleeds to the hero root's outer padding-box
          // edges even when the hero has its own vertical
          // padding configured.
          ...(photoBreakoutStyle || {}),
        }}
        aria-hidden="true"
      >
        {images.map((img, idx) => {
          const src = img.cropped_image_url || img.image_url;
          const { className: filterClass, style: filterStyle } =
            getHeroImageStyle(img);
          const points = resolvePolygonPoints({
            modeId,
            imageIndex: idx,
            imageCount,
          });
          const { wrapperStyle, clipPath } = resolvePolygonGeometry(points);
          return (
            <div
              key={`diag-${img.id || idx}-${idx}`}
              className={`hero-diagonal-split-photo hero-diagonal-split-photo--${
                imageCount === 2 ? (idx === 0 ? "primary" : "secondary") : "primary"
              }`}
              style={{
                ...wrapperStyle,
                clipPath,
                WebkitClipPath: clipPath,
              }}
              data-hero-diagonal-photo={
                imageCount === 2 ? (idx === 0 ? "primary" : "secondary") : "primary"
              }
            >
              {src ? (
                <ProgressiveHeroImage
                  src={src}
                  alt={img.alt_text || ""}
                  loading={idx === 0 ? "eager" : "lazy"}
                  draggable={false}
                  className={`hero-diagonal-split-img ${filterClass}`.trim()}
                  style={filterStyle}
                />
              ) : (
                <div className="hero-diagonal-split-img hero-diagonal-split-img--empty" />
              )}
              <EditableSlotMediaOverlay slotData={img} heroItem={item} />
            </div>
          );
        })}
      </div>
      <SlotCapacityAffordance
        heroSlots={heroSlots}
        heroId={item?.id}
        slotName="gallery"
        currentCount={images.length}
        className="absolute left-1/2 top-6 z-[2] w-full max-w-xs -translate-x-1/2 px-4"
      />

      {/* Text column. ``relative z-10`` lifts it above the photo
          layer (which is ``z-0`` via the CSS rule). The asymmetric
          padding from ``resolveTextInset`` reserves the photo
          polygon's space so heading / body / CTA never cross the
          visible photo edge.
          
          IMPORTANT: this container is intentionally ``w-full``
          with NO ``max-w-*`` cap. The inset padding is expressed
          as PERCENTAGES of the container's width, and percentage
          padding only computes correctly against the actual
          container's containing-block width. An earlier version
          ran ``w-full max-w-3xl mx-auto`` which capped the inner
          box at 768px while still computing % padding against
          the full-bleed parent — on hero widths > 1024px the
          padding sum would exceed 768px, forcing the heading to
          overflow the inner box and visually cross into the
          photo regions on either side. Letting the container
          take the full hero width ties the % padding's reference
          frame to the same box that contains the text, so the
          asymmetric inset reserves the photo's exact horizontal
          extent and the text content area equals the photo gap
          to the pixel. Line length is naturally constrained by
          the inset itself (text usable width = 100% - 2*inset),
          which scales with the photo geometry. */}
      <div
        className={
          responsiveHeight
            ? "relative z-10 flex items-center justify-center w-full"
            : "absolute inset-0 z-10 flex items-center justify-center"
        }
        style={responsiveHeight ? { minHeight: `${baseHeightPx}px` } : undefined}
      >
        <div
          ref={contentAnim.ref}
          className={`flex flex-col gap-4 w-full ${contentAnim.className}`}
          style={{
            paddingTop: config.contentPy,
            paddingBottom: config.contentPy,
            ...contentAnim.style,
          }}
        >
          <SlotRenderer
            slotName="heading"
            heroSlots={heroSlots}
            heroBase={heroBase}
            heroId={item?.id}
            slotIndex={1}
            className={slotClassName}
            style={{ ...slotInsetFor("heading"), textAlign: contentAlignment }}
          />
          <SlotRenderer
            slotName="body"
            heroSlots={heroSlots}
            heroBase={heroBase}
            heroId={item?.id}
            slotIndex={2}
            className={slotClassName}
            style={{ ...slotInsetFor("body"), textAlign: contentAlignment }}
          />
          <SlotRenderer
            slotName="cta"
            heroSlots={heroSlots}
            heroBase={heroBase}
            heroId={item?.id}
            slotIndex={3}
            className={slotClassName}
            style={{ ...slotInsetFor("cta"), textAlign: contentAlignment }}
          />
        </div>
      </div>
    </div>
  );
};

export default React.memo(HeroDiagonalSplit);
